import {
  SellerOrder as SellerOrderType,
  SellerOrderStatus,
} from '@shopify-multiseller/graphql/partner/types';
import type { TimelineEntry } from '../../components/Timeline';
import { formatDate } from '../../helpers/formatters';
import { isPayoutPaid, type PayoutMethodState } from './payoutState';

/**
 * One format for every date on the timeline.
 *
 * There were three — a date and time for placing, a bare date for fulfilment,
 * and "Paid Mar 20" for the payout — so four rows of the same short list
 * stated their times three different ways.
 */
const when = (date?: string | null): string | undefined =>
  date
    ? formatDate(date, { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' })
    : undefined;

/** Statuses where the order stopped before it could be fulfilled. */
export const TERMINAL_FAILURES: SellerOrderStatus[] = [
  SellerOrderStatus.Rejected,
  SellerOrderStatus.RejectedThirdParty,
  SellerOrderStatus.AcceptTimeout,
  SellerOrderStatus.FulfillmentTimeout,
  SellerOrderStatus.Cancelled,
];

/**
 * The order's arc as four steps: created, accepted, fulfilled, paid.
 *
 * A status badge answers "where is it now"; opening an order is usually a
 * question about where it has been and what is still owed.
 *
 * Every row names what happened and its detail says when. A step that has
 * happened but carries no timestamp shows no detail at all — the fallback
 * used to repeat the label, so a row read "Accepted" over "Accepted". The
 * timestamps are nullable by design (orders placed before the lifecycle
 * fields existed have none) and the dot already says the step is done.
 */
export const buildOrderTimeline = (
  order: SellerOrderType,
  /**
   * Whether money can reach this seller at all. Passed in rather than read
   * here so this stays a pure function of what it is given — the caller has
   * the seller, this has the order.
   */
  payoutMethod: PayoutMethodState = 'ready',
): TimelineEntry[] => {
  const status = order.status;
  const failed = TERMINAL_FAILURES.includes(status);
  const accepted =
    status === SellerOrderStatus.PendingFulfillment ||
    status === SellerOrderStatus.WaitingThirdParty ||
    status === SellerOrderStatus.Fulfilled ||
    status === SellerOrderStatus.FulfillmentTimeout ||
    status === SellerOrderStatus.Cancelled ||
    !!order.acceptedAt;
  const fulfilled = status === SellerOrderStatus.Fulfilled || !!order.fulfilledAt;
  const paid = isPayoutPaid(order);

  const acceptedDetail = () => {
    if (status === SellerOrderStatus.Rejected) return 'Rejected';
    if (status === SellerOrderStatus.RejectedThirdParty) return 'Rejected by another seller';
    if (status === SellerOrderStatus.AcceptTimeout) return 'Not accepted in time';
    if (accepted) return when(order.acceptedAt);
    return 'Waiting on you';
  };

  const fulfilledDetail = () => {
    if (status === SellerOrderStatus.Cancelled) return 'Cancelled';
    if (status === SellerOrderStatus.FulfillmentTimeout) return 'Not fulfilled in time';
    // The date only. The carrier is on the tracking card, beside the tracking
    // number it belongs to, and here it was a second kind of fact in a column
    // of dates.
    if (fulfilled) return when(order.fulfilledAt);
    if (status === SellerOrderStatus.WaitingThirdParty) return 'Waiting on other sellers';
    if (accepted) return 'Waiting on you';
    return undefined;
  };

  const paidDetail = () => {
    if (paid) return when(order.payout?.payoutDate);
    // "Not payable" was the whole explanation, and it read as a fault with the
    // payment rather than as a consequence of the order having stopped. Say
    // which it is: this order went back to the shop, so there is nothing to
    // pay out on it.
    if (failed) return 'No payout — this order went back to the shop';
    // The other reason money does not move: there is nowhere to send it. The
    // screen used to say "Pending" either way, so a seller with no payout
    // method was told to wait for something that was never going to happen.
    if (payoutMethod !== 'ready') {
      return payoutMethod === 'missing' ? 'Set up payouts to be paid' : 'Finish setting up payouts';
    }
    // No due date. It would be a second date format for a date that has not
    // happened, and the payout card on the same screen already says when it
    // is expected.
    return 'Pending';
  };

  const steps: TimelineEntry[] = [
    {
      key: 'placed',
      // "Created", matching the word the rest of the app uses for when a
      // record came into being — the orders list filters on it too.
      label: 'Created',
      detail: when(order.orderDate),
      state: 'done',
    },
    {
      key: 'accepted',
      label: 'Accepted',
      detail: acceptedDetail(),
      state:
        status === SellerOrderStatus.Rejected ||
        status === SellerOrderStatus.RejectedThirdParty ||
        status === SellerOrderStatus.AcceptTimeout
          ? 'failed'
          : accepted
            ? 'done'
            : 'current',
    },
    {
      key: 'fulfilled',
      label: 'Fulfilled',
      detail: fulfilledDetail(),
      state:
        status === SellerOrderStatus.FulfillmentTimeout || status === SellerOrderStatus.Cancelled
          ? 'failed'
          : fulfilled
            ? 'done'
            : accepted
              ? 'current'
              : 'upcoming',
    },
    {
      key: 'payout',
      // "Paid", not "Payout": the other three rows name something that
      // happened, and a noun among three participles read as the odd one out.
      label: 'Paid',
      detail: paidDetail(),
      // Nothing is stopping the seller from fixing this one, so the row is
      // the way to fix it rather than a description of the problem.
      href: !paid && !failed && payoutMethod !== 'ready' ? '/settings/payout' : undefined,
      state: paid ? 'done' : failed ? 'upcoming' : fulfilled ? 'current' : 'upcoming',
    },
  ];

  // Where it stopped, it stops. Review: "when something is rejected, maybe we
  // can just have the remainder of the timeline appear to fade away, not
  // showing the later stages." A rejected order was still drawing Fulfilled
  // and Paid as hollow steps, which reads as a plan — they are not waiting to
  // happen, they are never going to. The rail fades out under the last step
  // instead (see Timeline), so the list reads as cut short rather than ended.
  const stoppedAt = steps.findIndex((step) => step.state === 'failed');
  return stoppedAt === -1 ? steps : steps.slice(0, stoppedAt + 1);
};
